import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Eye, EyeOff, ArrowLeft, Lock } from "lucide-react";
import { authAPI } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import BackgroundWrapper from "../../components/common/BackgroundWrapper";
import Button from "../../components/common/Button";
import toast from "react-hot-toast";
import "./Auth.css";

const ResetPassword = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { resettoken } = useParams();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    watch,
  } = useForm();

  const password = watch("password");

  useEffect(() => {
    console.log("Reset token from URL:", resettoken);
    if (!resettoken) {
      toast.error("Invalid reset link");
      navigate("/login");
    }
  }, [resettoken, navigate]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      console.log("Attempting password reset with token:", resettoken);

      const response = await authAPI.resetPassword(resettoken, data.password);

      console.log("Reset response:", response.data);

      if (response.data.success) {
        toast.success("Password reset successful!");

        if (response.data.token && response.data.user) {
          localStorage.setItem("token", response.data.token);
          localStorage.setItem("user", JSON.stringify(response.data.user));

          setTimeout(() => {
            window.location.href = "/";
          }, 1000);
        } else {
          navigate("/login");
        }
      }
    } catch (error) {
      console.error("Password reset error:", error);
      const message =
        error.response?.data?.message || "Failed to reset password";
      toast.error(message);
      setError("root", { message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <BackgroundWrapper background="/auth-grid.png">
      <div className="auth-wrapper">
        <div className="auth-card">
          {/* Back Button */}
          <button onClick={() => navigate("/login")} className="back-btn">
            <ArrowLeft size={20} />
          </button>

          {/* Icon */}
          <div className="auth-success-icon">
            <Lock size={60} />
          </div>

          {/* Title */}
          <h2 className="auth-title-small">Set New Password</h2>
          <p className="auth-subtext">
            Enter a new secure password you’ll remember.
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)}>
            {errors.root && (
              <div className="error-message">{errors.root.message}</div>
            )}

            {/* NEW PASSWORD */}
            <div className="password-wrapper">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                className={`auth-input ${errors.password ? "error" : ""}`}
                placeholder="New Password"
                {...register("password", {
                  required: "Password is required",
                  minLength: {
                    value: 6,
                    message: "Password must be at least 6 characters",
                  },
                })}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.password && (
              <span className="form-error">{errors.password.message}</span>
            )}

            {/* CONFIRM PASSWORD */}
            <div className="password-wrapper">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                className={`auth-input ${
                  errors.confirmPassword ? "error" : ""
                }`}
                placeholder="Confirm New Password"
                {...register("confirmPassword", {
                  required: "Please confirm your password",
                  validate: (value) =>
                    value === password || "Passwords do not match",
                })}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.confirmPassword && (
              <span className="form-error">
                {errors.confirmPassword.message}
              </span>
            )}

            {/* Submit */}
            <Button
              type="submit"
              className="auth-submit"
              loading={loading}
              disabled={loading}
            >
              Reset Password
            </Button>
          </form>

          {/* Footer */}
          <div className="auth-footer">
            Remember your password?{" "}
            <Link to="/login" className="auth-link">
              Sign In
            </Link>
          </div>
        </div>
      </div>
    </BackgroundWrapper>
  );
};

export default ResetPassword;
